import { useState } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { apiRequest } from "@/lib/queryClient";
import { useCustomer } from "@/lib/customer-context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Wallet as WalletIcon,
  Plus,
  Download,
  TrendingUp,
  ArrowDownRight,
} from "lucide-react";
import { WalletTopUpDialog } from "@/components/customer/wallet-topup-dialog";
import { AnimatedBalance } from "@/components/customer/animated-balance";
import { StatCard } from "@/components/customer/stat-card";
import { SpendingChart } from "@/components/customer/spending-chart";
import { TransactionTimeline } from "@/components/customer/transaction-timeline";
import { prepareSpendingData } from "@/lib/chart-utils";
import { pageTransition, staggerContainer } from "@/lib/animations";

interface WalletTransaction {
  id: string;
  type: "CREDIT" | "DEBIT";
  amountCents: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string | null;
  merchantName?: string;
  reference: string | null;
  createdAt: string;
}

export default function PortalWallet() {
  const { customer } = useCustomer();
  const { formatCurrency } = useOrgSettings();
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);

  const { data: transactions = [], isLoading } = useQuery<WalletTransaction[]>({
    queryKey: ["customer-wallet-transactions"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customer/wallet/transactions");
      if (!response.ok) throw new Error("Failed to fetch transactions");
      return response.json();
    },
  });

  const walletBalance = parseFloat(customer?.walletBalance || "0") * 100; // Convert to paise
  const totalCredits = transactions.filter(t => t.type === "CREDIT").reduce((sum, t) => sum + t.amountCents, 0);
  const totalDebits = transactions.filter(t => t.type === "DEBIT").reduce((sum, t) => sum + t.amountCents, 0);
  
  // Prepare chart data
  const spendingData = prepareSpendingData(transactions, 30);
  const netBalance = (totalCredits - totalDebits) / 100;

  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      className="p-4 md:p-6 space-y-6 md:space-y-8 max-w-7xl mx-auto"
    >

      {/* Hero Balance Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/10 via-purple-500/5 to-transparent min-h-[400px]"
      >
        {/* Mesh background */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary) / 0.2) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Glassmorphism card */}
        <div className="relative z-10 p-6 md:p-10">
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-4">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur flex items-center justify-center"
              >
                <WalletIcon className="h-8 w-8 md:h-10 md:w-10 text-primary" />
              </motion.div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Your Wallet</p>
                <h1 className="text-2xl md:text-3xl font-bold">Available Balance</h1>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <AnimatedBalance value={walletBalance} size="xl" />

            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => setShowTopUpDialog(true)}
                className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all"
              >
                <Plus className="h-5 w-5 mr-2" />
                Top Up via UPI
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-background/50 backdrop-blur"
              >
                <Download className="h-5 w-5 mr-2" />
                Export Transactions
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-6 border-t">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Added</p>
                <p className="text-xl md:text-2xl font-bold text-green-600 flex items-center gap-1">
                  <ArrowDownRight className="h-5 w-5" />
                  {formatCurrency(totalCredits)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                <p className="text-xl md:text-2xl font-bold text-red-600 flex items-center gap-1">
                  <TrendingUp className="h-5 w-5" />
                  {formatCurrency(totalDebits)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Transaction Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Transaction History</h2>
          <p className="text-muted-foreground">
            {transactions.length} transaction{transactions.length !== 1 ? "s" : ""}
          </p>
        </div>

        {transactions.length > 0 ? (
          <TransactionTimeline
            transactions={transactions}
            groupByDate
            showDetails
          />
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">No Transactions Yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Top up your wallet to get started
              </p>
              <Button onClick={() => setShowTopUpDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Top Up Now
              </Button>
            </CardContent>
          </Card>
        )}
      </motion.div>

      <WalletTopUpDialog
        open={showTopUpDialog}
        onOpenChange={setShowTopUpDialog}
      />
    </motion.div>
  );
}
